import React from 'react';
import { Select, Table, Tag } from 'antd';
import { CheckboxFilter } from 'components/Filters/CheckboxFilter';
import { SumRange } from 'components/Filters/SumRange';
import {
  COLUMN_EXTRA_LARGE,
  COLUMN_EXTRA_SMALL,
  COLUMN_MEDIUM,
  COLUMN_PRE_LARGE,
  PAGE_SIZE_OPTIONS,
  SCROLL_Y_WITH_SUMMARY,
} from 'constants/table';
import { TableSearchPanel } from 'ui/TableSearchPanel';
import { formatDate } from 'utils';
import { renderWithTooltip } from 'utils/table';
import { DatePickerFilter } from 'components/Filters/DatePickerFilter';
import { FilterIcon } from 'components/Filters/FilterIcon';
import { InputFilter } from 'components/Filters/InputFilter';
import { RangePickerFilter } from 'components/Filters/RangePickerFilter';
import { thousandFormatter } from 'utils/forms/formatter';
import {
  clientBoughtStatus,
  clientBoughtStatusToColor,
} from 'utils/handbooks/clientBoughtHandbooks';
import { SelectTableFilter } from 'components/Filters/SelectTableFilter';

function HistoryTable({
  items,
  filters,
  page,
  pageSize,
  loading,
  summary,
  totalRows,
  onRowClick,
  handleChange,
  selectedRows,
  onChange,
  onFilter,
  packageServices = [],
}) {
  const {
    status,
    type,
    f_price,
    t_price,
    f_bought,
    t_bought,
    package_ids,
    start_date,
    end_date,
    days_left,
    creator_role_names,
    creator_full_name,
  } = filters;

  const columns = [
    {
      width: COLUMN_EXTRA_LARGE,
      title: 'Клиент',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (value) => renderWithTooltip(value, 60),
    },
    {
      width: COLUMN_PRE_LARGE,
      title: 'Услуга',
      dataIndex: 'package_title',
      key: 'package_title',
      filterDropdown: () => {
        return (
          <TableSearchPanel>
            <Select
              mode="multiple"
              allowClear
              showArrow
              placeholder="Выбрать услугу"
              value={package_ids}
              onChange={(package_ids) => onFilter({ package_ids })}
              style={{ width: '180px' }}
            >
              {packageServices.map(({ title, id }) => (
                <Select.Option key={id} value={id}>
                  {title}
                </Select.Option>
              ))}
            </Select>
          </TableSearchPanel>
        );
      },
      filterIcon: () => <FilterIcon isActive={package_ids.length} />,
    },
    {
      width: COLUMN_EXTRA_SMALL,
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        return (
          <Tag color={type === 'package' ? 'purple' : ''}>
            {type === 'package' ? 'Тариф' : 'Услуга'}
          </Tag>
        );
      },
      filterDropdown: () => (
        <CheckboxFilter
          filter={type}
          filters={[
            { text: 'Тариф', value: 'package' },
            { text: 'Услуга', value: 'service' },
          ]}
          onFilterChange={(type) => onFilter({ type })}
        />
      ),
      filterIcon: () => <FilterIcon isActive={type} />,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Опция',
      dataIndex: 'option_title',
      key: 'option_title',
    },

    {
      width: COLUMN_MEDIUM,
      title: 'Изначальная цена (₸)',
      dataIndex: 'origin_price',
      key: 'origin_price',
    },

    {
      width: COLUMN_MEDIUM,
      title: 'Скидка',
      dataIndex: 'discount_percent',
      key: 'discount_percent',
      render: (i) => (i ? `${i}%` : '0%'),
    },

    {
      width: COLUMN_MEDIUM,
      title: 'Цена продажи (₸)',
      dataIndex: 'price',
      key: 'price',
      filterDropdown: () => (
        <SumRange
          fromValue={f_price}
          toValue={t_price}
          getRange={(first, second) =>
            onFilter({ f_price: first, t_price: second })
          }
        />
      ),
      filterIcon: () => <FilterIcon isActive={f_price || t_price} />,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Дата покупки',
      dataIndex: 'created_date',
      key: 'created_date',
      render: (date) => formatDate(date, 'DD.MM.YYYY, HH.mm'),
      filterDropdown: () => (
        <RangePickerFilter
          value={[f_bought, t_bought]}
          onFilterChange={([start, end]) => {
            onFilter({ f_bought: start, t_bought: end });
          }}
        />
      ),
      filterIcon: () => <FilterIcon isActive={f_bought && t_bought} />,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Дата начала',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (value) => formatDate(value),
      filterDropdown: () => {
        return (
          <DatePickerFilter
            value={start_date}
            getDate={(start_date) => onFilter({ start_date })}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={start_date} />,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Дата окончания',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (value) => formatDate(value),
      filterDropdown: () => {
        return (
          <DatePickerFilter
            value={end_date}
            getDate={(end_date) => onFilter({ end_date })}
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={end_date} />,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Оставшиеся дни',
      dataIndex: 'days_left',
      key: 'days_left',
      filterDropdown: () => (
        <InputFilter
          value={days_left}
          placeholder="Поиск по дням"
          onFilterChange={(days_left) => onFilter({ days_left })}
        />
      ),
      filterIcon: () => <FilterIcon search isActive={days_left} />,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const text = clientBoughtStatus[status] || '';
        const color = clientBoughtStatusToColor[status] || 'default';

        return <Tag color={color}>{text}</Tag>;
      },
      filterDropdown: () => (
        <CheckboxFilter
          filter={status}
          filters={[
            { text: 'Активный', value: 'active' },
            { text: 'Не активный', value: 'inactive' },
            { text: 'Отключен', value: 'off' },
          ]}
          onFilterChange={(status) => onFilter({ status })}
        />
      ),
      filterIcon: () => <FilterIcon isActive={status} />,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Оформил',
      dataIndex: 'creator_full_name',
      key: 'creator_full_name',
      filterDropdown: () => (
        <InputFilter
          multiple
          value={creator_full_name}
          filter={creator_full_name}
          placeholder="Оформил"
          onFilterChange={(creator_full_name) =>
            onFilter({ creator_full_name })
          }
        />
      ),
      filterIcon: () => <FilterIcon />,
    },
    {
      width: COLUMN_MEDIUM,
      title: 'Роль',
      dataIndex: 'creator_role_names',
      key: 'creator_role_names',
      render: (i) => i?.join(', '),

      filterDropdown: () => (
        <SelectTableFilter
          multiple
          value={creator_role_names}
          filter={creator_role_names}
          placeholder="Выберите роли"
          dataSourse={[
            { key: 'Бухгалтер', value: 'Бухгалтер' },
            { key: 'Админ', value: 'Админ' },
            { key: 'Менеджер продажи', value: 'Менеджер продажи' },
          ]}
          getSelectedItem={(creator_role_names) =>
            onFilter({ creator_role_names })
          }
        />
      ),
      filterIcon: () => <FilterIcon isActive={creator_role_names.length} />,
    },
  ];

  const renderSummary = () => {
    const { total_amount, total_amount_origin } = summary;

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell>&nbsp;</Table.Summary.Cell>
          <Table.Summary.Cell>Итого</Table.Summary.Cell>
          <Table.Summary.Cell>&nbsp;</Table.Summary.Cell>
          <Table.Summary.Cell>&nbsp;</Table.Summary.Cell>
          <Table.Summary.Cell>&nbsp;</Table.Summary.Cell>
          <Table.Summary.Cell>
            {thousandFormatter(total_amount_origin)} ₸
          </Table.Summary.Cell>
          <Table.Summary.Cell />
          <Table.Summary.Cell>
            {thousandFormatter(total_amount)} ₸
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <Table
      columns={columns}
      dataSource={items}
      loading={loading}
      scroll={{ x: 1300, y: SCROLL_Y_WITH_SUMMARY }}
      summary={summary ? renderSummary : null}
      pagination={{
        total: totalRows,
        showSizeChanger: true,
        current: page,
        pageSize,
        pageSizeOptions: [
          ...PAGE_SIZE_OPTIONS,
          totalRows > 50 ? totalRows : 100,
        ],
      }}
      rowKey="id"
      rowSelection={{
        type: 'checkbox',
        selectedRowKeys: selectedRows,
        onChange: (id) => handleChange(id),
      }}
      onRow={(record) => {
        if (onRowClick) {
          return {
            onDoubleClick: () => onRowClick(record),
          };
        }

        return null;
      }}
      onChange={onChange}
    />
  );
}

export { HistoryTable };
